import { FeedbackKind, FeedbackStatus, REVIEW_SITES, ReviewInvite } from '@/enums/feedback'
import { API_BASE_URL } from '@/libs/apiBase'
import { intlLocale } from '@/libs/dayjs'
import i18n from '@/libs/i18n'
import type { TagItem } from '@/types/Common'
import type { IFeedback, IFeedbackSettings, IFeedbackSummary, IStatDelta } from '@/types/Feedback'

/*
 * GUEST FEEDBACK in the console (docs/API.md §18): the words and colours of a status and a kind,
 * the ready sentences, the list's order, the private photo address and the settings' defaults.
 */

export const feedbackStatusTag = (status: string): TagItem => {
    const label = i18n.global.t(`feedback_status.${status}`, status)

    switch (status) {
        case FeedbackStatus.New:        return { label, color: 'warning', variant: 'solid', icon: 'i-lucide-bell-ring' }
        case FeedbackStatus.InProgress: return { label, color: 'info' }
        case FeedbackStatus.Resolved:   return { label, color: 'success', icon: 'i-lucide-check' }
        default:                        return { label, color: 'neutral' }
    }
}

/** A kind's colour and icon: praise `success`, a complaint `error`, a suggestion `info`; a kind this console does not know is neutral, under its own name. */
export const feedbackKindLook = (kind: string): { color: 'success' | 'error' | 'info' | 'neutral', icon: string } => {
    switch (kind) {
        case FeedbackKind.Praise:     return { color: 'success', icon: 'i-lucide-thumbs-up' }
        case FeedbackKind.Complaint:  return { color: 'error', icon: 'i-lucide-thumbs-down' }
        case FeedbackKind.Suggestion: return { color: 'info', icon: 'i-lucide-lightbulb' }
        default:                      return { color: 'neutral', icon: 'i-lucide-message-square' }
    }
}

export const feedbackKindLabel = (kind: string): string => i18n.global.t(`feedback_kind.${kind}`, kind)

export const feedbackKindTag = (kind: string): TagItem => ({ label: feedbackKindLabel(kind), variant: 'subtle', ...feedbackKindLook(kind) })

/** Only praise is rated: a complaint and a suggestion show no stars, also one stored with a rating before (§18.2). */
export const feedbackRated = (kind: string): boolean => kind === FeedbackKind.Praise

export const feedbackCategoryLabel = (category: string): string => i18n.global.t(`feedback_category.${category}`, category)

/** A ready sentence in the console's language (the texts of §18.3). */
export const feedbackPresetLabel = (preset: string): string => i18n.global.t(`feedback_preset.${preset}`, preset)

export const reviewSiteLabel = (site: string): string => i18n.global.t(`review_site.${site}`, site)

/** Still to be handled: new or in progress. */
export const isFeedbackOpen = (feedback: IFeedback): boolean => feedback.status !== FeedbackStatus.Resolved

/** The list's rank among the open ones: a complaint during the stay, another complaint, a suggestion, praise. */
const openRank = (feedback: IFeedback): number => {
    if (feedback.kind === FeedbackKind.Complaint) return feedback.during_stay ? 0 : 1
    if (feedback.kind === FeedbackKind.Suggestion) return 2

    return 3
}

/**
 * The API's order (§18.4): the open ones first — complaints during the stay, then other
 * complaints, then suggestions, then praise, each oldest first — then the rest newest first.
 */
export const compareFeedback = (a: IFeedback, b: IFeedback): number => {
    const openA = isFeedbackOpen(a)
    const openB = isFeedbackOpen(b)

    if (openA !== openB) return openA ? -1 : 1
    if (!openA) return b.created_at.localeCompare(a.created_at)

    const rank = openRank(a) - openRank(b)

    return rank !== 0 ? rank : a.created_at.localeCompare(b.created_at)
}

/**
 * A summary with its `suggestions` counts filled: an API from before suggestions (§18.4) sends
 * none, and every count the console draws is a number.
 */
export const withSuggestions = (summary: IFeedbackSummary): IFeedbackSummary => ({
    ...summary,
    suggestions: summary.suggestions ?? 0,
    previous:    { ...summary.previous, suggestions: summary.previous?.suggestions ?? 0 },
    trend:       { ...summary.trend, points: (summary.trend?.points ?? []).map((point) => ({ ...point, suggestions: point.suggestions ?? 0 })) },
    by_category: (summary.by_category ?? []).map((row) => ({ ...row, suggestions: row.suggestions ?? 0 })),
    top_rooms:   (summary.top_rooms ?? []).map((row) => ({ ...row, suggestions: row.suggestions ?? 0 })),
})

/** One photo of a feedback: private, read with the `JWT` cookie (an `<img>` sends no header). */
export const feedbackPhotoUrl = (tid: string, id: string, index: number): string =>
    `${API_BASE_URL}/tenants/${tid}/feedback/${id}/photos/${index}`

/** What one line of the list says: the text, else the ready sentences. */
export const feedbackSummary = (feedback: IFeedback): string =>
    feedback.text || feedback.presets.map(feedbackPresetLabel).join(' · ')

/** A median time in minutes as people say it: "under a minute", "42 min", "1 h 20 min", "2 d 3 h"; '–' without one. */
export const formatMinutes = (minutes: number | null): string => {
    if (minutes === null) return '–'
    if (minutes < 1) return i18n.global.t('feedback_analysis.under_minute')
    if (minutes < 60) return i18n.global.t('feedback_analysis.minutes', { m: minutes })

    const hours = Math.floor(minutes / 60)

    if (hours < 24) return i18n.global.t('feedback_analysis.hours', { h: hours, m: minutes % 60 }).replace(/ 0 min$/, '')

    return i18n.global.t('feedback_analysis.days', { d: Math.floor(hours / 24), h: hours % 24 }).replace(/ 0 h$/, '')
}

/** A share of a whole as a whole percent ("27 %" / "27%"); '' when the whole is 0. */
export const formatShare = (part: number, whole: number): string => {
    if (whole <= 0) return ''

    return new Intl.NumberFormat(intlLocale(), { style: 'percent', maximumFractionDigits: 0 }).format(part / whole)
}

/** A rating with one decimal: "4,5". */
export const formatRating = (value: number): string =>
    new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(value)

/**
 * A figure's change against the period before, as a stat tile shows it ("+2 glede na prejšnjih
 * 30 dni"): `upGood` true when more is better, false when worse, null when neither (the tone
 * stays muted); null when there is nothing to compare with.
 */
export const statDelta = (now: number | null, before: number | null, days: number, upGood: boolean | null, decimals = 0): IStatDelta | null => {
    if (now === null || before === null) return null

    const change = Math.round((now - before) * 10 ** decimals) / 10 ** decimals

    if (change === 0) return { text: i18n.global.t('feedback_analysis.delta_same', { n: days }), tone: 'neutral', direction: 'flat' }

    const amount = new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: decimals, minimumFractionDigits: decimals, signDisplay: 'always' }).format(change)
    const better = upGood === null ? null : (change > 0) === upGood

    return {
        text:      i18n.global.t('feedback_analysis.delta', { change: amount, n: days }),
        tone:      better === null ? 'neutral' : better ? 'good' : 'bad',
        direction: change > 0 ? 'up' : 'down',
    }
}

/** The settings of a tenant that never saved them (the API's defaults, §18.1). */
export const defaultFeedbackSettings = (): IFeedbackSettings => ({
    enabled:        true,
    retention_days: 400,
    max_per_day:    10,
    review:         {
        links:          Object.fromEntries(REVIEW_SITES.map((site) => [site, ''])),
        invite:         [ReviewInvite.AfterFeedback, ReviewInvite.DepartureDay],
        departure_from: '18:00',
    },
})
